import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../config/api';
import {
  Box, Paper, Typography, Button, CircularProgress, Chip, Divider, Alert,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  CheckCircle as CheckCircleIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PortalBrandingHeader from './PortalBrandingHeader';

/**
 * Página pública de subida de documentos (link mágico con token, sin login).
 * El token identifica la postulación; lo que se sube queda registrado contra ella.
 */
const PortalDocumentsScreen = () => {
  const { token } = useParams();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null); // { candidateName, positionName, items }
  const [uploading, setUploading] = useState({}); // { [docId]: bool }
  const fileInputs = useRef({});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await api.get(`/public/document-request/${token}`);
      const d = res.data?.response?.data;
      if (!d) setError(true);
      else setData(d);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePick = (docId) => {
    const input = fileInputs.current[docId];
    if (input) input.click();
  };

  const handleFile = async (docId, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading((p) => ({ ...p, [docId]: true }));
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('docId', docId);
      const res = await api.post(
        `/public/document-request/${token}/upload`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const uploaded = res.data?.response?.data || {};
      setData((prev) => ({
        ...prev,
        items: (prev.items || []).map((it) =>
          it.docId === docId
            ? { ...it, status: 'uploaded', fileName: uploaded.fileName || file.name }
            : it
        ),
      }));
    } catch (err) {
      console.error(err);
      // eslint-disable-next-line no-alert
      alert(t('portal.documents.uploadError'));
    } finally {
      setUploading((p) => ({ ...p, [docId]: false }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box
        sx={{
          minHeight: '100vh', backgroundColor: '#F9FAFB',
          display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>{t('portal.documents.invalidTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('portal.documents.invalidBody')}</Typography>
        </Paper>
      </Box>
    );
  }

  const items = data.items || [];
  const pendingRequired = items.filter((it) => it.required && it.status !== 'uploaded').length;
  const allDone = items.length > 0 && items.every((it) => it.status === 'uploaded');

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <PortalBrandingHeader branding={data.branding} />
      <Box sx={{ py: { xs: 3, md: 6 }, px: 2 }}>
        <Paper sx={{ maxWidth: 640, mx: 'auto', p: { xs: 3, md: 4 } }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {t('portal.documents.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {data.candidateName
            ? `${t('portal.documents.greeting')} ${data.candidateName},`
            : `${t('portal.documents.greeting')},`}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {data.positionName
            ? t('portal.documents.introWithPosition', { position: data.positionName })
            : t('portal.documents.intro')}
        </Typography>

        {allDone && (
          <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success" sx={{ mb: 3 }}>
            {t('portal.documents.allDone')}
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((it) => {
            const isUploaded = it.status === 'uploaded';
            const isUploading = !!uploading[it.docId];
            return (
              <Box key={it.docId} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2">
                      {it.label}
                      {it.required && (
                        <Typography component="span" color="error.main"> *</Typography>
                      )}
                    </Typography>
                    {it.instructions ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {it.instructions}
                      </Typography>
                    ) : null}
                    {isUploaded && it.fileName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                        <FileIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {it.fileName}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {isUploaded && (
                    <Chip
                      size="small"
                      color="success"
                      icon={<CheckCircleIcon />}
                      label={t('portal.documents.uploaded')}
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <input
                    type="file"
                    hidden
                    ref={(el) => { fileInputs.current[it.docId] = el; }}
                    onChange={(e) => handleFile(it.docId, e)}
                  />
                  <Button
                    variant={isUploaded ? 'outlined' : 'contained'}
                    size="small"
                    startIcon={isUploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                    onClick={() => handlePick(it.docId)}
                    disabled={isUploading}
                  >
                    {isUploaded ? t('portal.documents.replace') : t('portal.documents.upload')}
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>

        {pendingRequired > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            {t('portal.documents.pendingRequired', { count: pendingRequired })}
          </Typography>
        )}
        </Paper>
      </Box>
    </Box>
  );
};

export default PortalDocumentsScreen;
